'use client';

import { useState } from 'react';
import { useParams } from 'next/navigation';
import { FormProvider, useForm } from 'react-hook-form';
import {
  useGetCompanyByIdQuery,
  useConvertDemoCompanyMutation,
} from 'src/api/redux';
import ControlledInput from 'src/components/ControlledFields/ControlledInput';
import { Dialog } from 'src/components/PulseUI/Dialog';
import { PulseButton } from 'src/components/PulseUI/PulseButton';
import { HubSpotCompanyField } from 'src/components/Settings/Companies/components/HubSpotCompanyField';
import { COMPANY_NAME_MAX_LENGTH_RULE } from 'src/components/Settings/Companies/components/HubSpotCompanyField/constants';
import { getCompanySaveError } from 'src/components/Settings/Companies/components/HubSpotCompanyField/utils';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';

interface ConvertDemoDialogProps {
  open: boolean;
  onClose: () => void;
}

interface ConvertDemoFormValues {
  name: string;
  hubspot_company_id: string | null;
  hubspot_company_name: string | null;
  hubspot_domain: string | null;
}

const ConvertDemoDialog = ({ open, onClose }: ConvertDemoDialogProps) => {
  const { companyId } = useParams<{ companyId: string }>();
  const log = useLogging();
  const { setNotification } = useNotification();

  // currentData, not data: a skipped query keeps returning the last company.
  const { currentData: currentCompany } = useGetCompanyByIdQuery(
    { companyId },
    { skip: !companyId }
  );
  const [convertDemoCompany] = useConvertDemoCompanyMutation();

  const [isLoading, setIsLoading] = useState(false);

  const methods = useForm<ConvertDemoFormValues>({
    values: {
      name: currentCompany?.name ?? '',
      hubspot_company_id: currentCompany?.hubspot_company_id ?? null,
      hubspot_company_name: currentCompany?.hubspot_company_name ?? null,
      hubspot_domain: currentCompany?.hubspot_domain ?? null,
    },
  });

  const {
    handleSubmit,
    reset,
    setError,
    formState: { isValid },
  } = methods;

  // The dialog stays mounted between opens, so every close puts the form back
  // to the company's saved values for the next one.
  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async ({
    name,
    ...hubspotLinkDetails
  }: ConvertDemoFormValues) => {
    if (!companyId) return;

    setIsLoading(true);
    try {
      // A customer must be linked to HubSpot (MKT-869). The link is always
      // sent; re-sending the demo's existing one leaves it unchanged.
      await convertDemoCompany({
        companyId,
        name: name?.trim() ?? '',
        ...hubspotLinkDetails,
      }).unwrap();

      log.event('Demo Company converted', {
        source: 'ConvertDemoDialog',
        companyId,
        hubspot_company_id: hubspotLinkDetails.hubspot_company_id,
      });
      setNotification({
        title: 'Success',
        message: 'Company converted to customer successfully',
        type: 'success',
      });
      handleClose();
    } catch (err: unknown) {
      const { message, isHubSpotLinkError } = getCompanySaveError(
        err,
        'Failed to convert demo company'
      );
      // The dialog stays open either way so no input is lost.
      if (isHubSpotLinkError) {
        setError('hubspot_company_id', { message });
      } else {
        log.exception('Failed to convert demo company', err);
        setNotification({
          title: 'Error',
          message,
          type: 'error',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      size="lg"
      isOpen={open}
      onClose={handleClose}
      // Sized to its content, like the company dialogs: the fixed size height
      // cuts off the HubSpot picker and linked-company card.
      className="!h-auto"
      showCloseButton
      aria-labelledby="convert-demo-dialog-title"
      aria-describedby="convert-demo-dialog-description"
      data-testid="convert-demo-dialog"
    >
      <Dialog.Header>
        <h2
          id="convert-demo-dialog-title"
          className="text-lg font-semibold"
          data-testid="convert-demo-dialog-header"
        >
          Convert to Customer
        </h2>
      </Dialog.Header>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} data-testid="convert-demo-form">
          <Dialog.Content
            className="p-6 flex flex-col gap-4"
            data-testid="convert-demo-dialog-content"
          >
            <p
              id="convert-demo-dialog-description"
              className="text-sm text-neutral-700"
            >
              Convert <strong>{currentCompany?.name}</strong> from a demo
              account to a full customer account. This will remove all demo
              users and data. A customer must be linked to its HubSpot record.
            </p>
            <HubSpotCompanyField isOpen={open} isEdit linkRequired />
            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold text-neutral-700">
                New Company Name
              </label>
              <ControlledInput
                name="name"
                label="New Company Name"
                placeholder="Enter company name"
                rules={{
                  validate: (value: string) =>
                    !!value?.trim() || 'This field is required',
                  maxLength: COMPANY_NAME_MAX_LENGTH_RULE,
                }}
                fullWidth
                useLabel={false}
              />
            </div>
          </Dialog.Content>
          <Dialog.Actions>
            <PulseButton
              variant="outline"
              color="neutral"
              onClick={handleClose}
              data-testid="convert-demo-dialog-cancel"
            >
              Cancel
            </PulseButton>
            <PulseButton
              type="submit"
              variant="solid"
              color="primary"
              disabled={!isValid}
              loading={isLoading}
              data-testid="convert-demo-dialog-confirm"
              aria-label={`Convert ${
                currentCompany?.name || 'company'
              } to customer`}
            >
              Convert
            </PulseButton>
          </Dialog.Actions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default ConvertDemoDialog;
